import { Link } from "react-router-dom";
import { useEffect } from "react";
import useLenis from "../hooks/useLenis";
import useSeo from "../hooks/useSeo";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Reveal } from "../lib/reveal";
import { caseStudies } from "../data/caseStudies";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

export default function CaseStudies() {
  useLenis();
  const { t } = useLang();
  useSeo({
    title: "Case Studies · Fresh Earth OED Deployments Across India",
    description:
      "Real OED deployments: Bhubaneswar bio-mining of 2.25 lakh tonnes, UltraTech Cement RDF odour control, Puri, Godda, Chas-Bokaro and Bio-CNG — before & after.",
    path: "/case-studies",
  });
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="bg-bone text-ink min-h-screen" data-testid="case-studies-page">
      <Navbar />
      <main className="pt-32 md:pt-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <Reveal className="flex items-center gap-3 mb-6">
            <span className="text-xs tracking-[0.22em] uppercase font-semibold text-moss">{t("Deployments", "तैनातियाँ")}</span>
          </Reveal>
          <Reveal>
            <h1 className="font-serif text-5xl md:text-7xl tracking-tighter leading-[0.95] text-ink max-w-4xl">
              {t("Restoring ground, city by city.", "भूमि की पुनर्स्थापना, शहर-दर-शहर।")}
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-base md:text-lg text-slate max-w-2xl">
              {t(
                "OED is deployed across municipal corporations, transfer stations, bio-mining projects and industrial sites. Explore the before & after.",
                "OED नगर निगमों, ट्रांसफर स्टेशनों, बायो-माइनिंग परियोजनाओं व औद्योगिक स्थलों पर तैनात है। पहले व बाद की स्थिति देखें।"
              )}
            </p>
          </Reveal>

          <div className="mt-16 space-y-5 pb-28">
            {caseStudies.map((c, i) => (
              <Reveal key={c.slug} delay={i * 0.05}>
                <Link
                  to={`/case-studies/${c.slug}`}
                  data-testid={`case-list-${c.slug}`}
                  className="group grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-center bg-sand rounded-3xl border border-stone/20 overflow-hidden hover:border-moss transition-colors duration-400"
                >
                  <div className="md:col-span-4 relative aspect-[16/10] md:aspect-[4/3] overflow-hidden">
                    <img src={c.afterImg} alt={c.city} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="md:col-span-6 p-2 md:py-8">
                    <div className="flex items-center gap-3 text-xs tracking-[0.12em] uppercase text-stone">
                      <span className="text-terracotta font-semibold">{c.tag}</span>
                      <span>·</span>
                      <span>{c.region}</span>
                    </div>
                    <h2 className="font-serif text-2xl md:text-3xl mt-2 text-ink leading-tight">{t(c.headline, c.hi?.headline)}</h2>
                    <p className="text-sm text-slate mt-3 max-w-xl">{t(c.summary, c.hi?.summary)}</p>
                  </div>
                  <div className="md:col-span-2 p-6 md:p-8 flex md:justify-end">
                    <div className="w-12 h-12 rounded-full bg-forest flex items-center justify-center group-hover:bg-moss transition-colors duration-400">
                      <ArrowUpRight size={20} className="text-bone" />
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
